import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  REQUIRED_VIEWPORTS,
  RESPONSIVE_SCREEN_KINDS,
  type ProjectDetailDTO,
  type ReviewScreenInput,
  type UiScreenDTO,
  type UiScreenStatus,
  type UiViewport,
} from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import {
  Btn,
  Chip,
  EmptyState,
  Panel,
  SectionHeader,
  inputClass,
  toneColor,
  withAlpha,
  type Tone,
} from "@/design/primitives";
import { canContribute, canManage, useProjectRole } from "../project-data";

const STATUS_TONE: Record<UiScreenStatus, Tone> = {
  PENDING: "amber",
  APPROVED: "green",
  CHANGES_REQUESTED: "red",
};

const STATUS_LABEL: Record<UiScreenStatus, string> = {
  PENDING: cs.pitch.statusPending,
  APPROVED: cs.pitch.statusApproved,
  CHANGES_REQUESTED: cs.pitch.statusChanges,
};

/** PITCH PERFECT: review smyčka nad screenshoty UI od workerů. */
export function PitchPerfectTab({ project }: { project: ProjectDetailDTO }) {
  const role = useProjectRole(project);
  const query = useQuery({
    queryKey: ["section", project.id, "screens"],
    queryFn: () => api.screens(project.id),
  });
  const [filter, setFilter] = useState<UiScreenStatus | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);

  const screens = query.data?.screens ?? [];
  const summary = query.data?.summary;
  const visible = screens.filter((s) => filter === null || s.status === filter);

  // Tabulka za každou aplikaci — server řadí dle slugu, screeny bez aplikace nakonec.
  const groups = useMemo(() => {
    const map = new Map<string, { slug: string | null; name: string; screens: UiScreenDTO[] }>();
    for (const s of visible) {
      const key = s.applicationSlug ?? "";
      let group = map.get(key);
      if (!group) {
        group = {
          slug: s.applicationSlug,
          name: s.applicationName ?? s.applicationSlug ?? cs.pitch.groupOther,
          screens: [],
        };
        map.set(key, group);
      }
      group.screens.push(s);
    }
    return [...map.values()];
  }, [visible]);

  // modal čte živá data z query, aby po review ukázal čerstvý stav
  const previewScreen = previewId ? (screens.find((s) => s.id === previewId) ?? null) : null;

  return (
    <div className="mt-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <SectionHeader label={cs.pitch.title} />
        <span className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
          {cs.pitch.subtitle}
        </span>
        <span className="ml-auto flex flex-wrap items-center justify-end gap-2">
          {summary && summary.total > 0 ? (
            <span
              className="text-[11px] font-bold"
              style={{
                color:
                  summary.approved === summary.total ? "var(--positive)" : "var(--text-secondary)",
              }}
              data-testid="pitch-summary"
            >
              {cs.pitch.summary(summary.approved, summary.total)}
            </span>
          ) : null}
          <FilterChip label={cs.pitch.filterAll} active={filter === null} onClick={() => setFilter(null)} />
          {(Object.keys(STATUS_LABEL) as UiScreenStatus[]).map((s) => (
            <FilterChip
              key={s}
              label={`${STATUS_LABEL[s]} (${screens.filter((x) => x.status === s).length})`}
              tone={STATUS_TONE[s]}
              active={filter === s}
              onClick={() => setFilter(filter === s ? null : s)}
            />
          ))}
        </span>
      </div>

      {visible.length === 0 ? (
        <Panel className="p-8">
          <EmptyState title={cs.pitch.empty} note={cs.pitch.emptyNote} />
        </Panel>
      ) : (
        <div className="flex flex-col gap-3">
          {groups.map((group) => (
            <AppScreensTable
              key={group.slug ?? "__other"}
              project={project}
              group={group}
              onPreview={setPreviewId}
              canDelete={canManage(role)}
            />
          ))}
        </div>
      )}

      {previewScreen ? (
        <ScreenPreviewModal
          project={project}
          screen={previewScreen}
          canReview={canContribute(role)}
          onClose={() => setPreviewId(null)}
        />
      ) : null}
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
  tone = "cyan",
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  tone?: Tone;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="cursor-pointer px-2.5 py-1 text-[11px] tracking-[0.1em]"
      style={{ borderRadius: "var(--radius-box)",
        border: `1px solid ${active ? toneColor[tone] : "var(--border-default)"}`,
        color: active ? toneColor[tone] : "var(--text-secondary)",
        background: active ? "color-mix(in srgb, var(--accent) 6%, transparent)" : "transparent",
      }}
    >
      {label}
    </button>
  );
}

const VIEWPORT_SHORT: Record<UiViewport, string> = { DESKTOP: "D", MOBILE: "M", TABLET: "T" };

function isResponsive(screen: UiScreenDTO) {
  return RESPONSIVE_SCREEN_KINDS.includes(screen.kind);
}

function shotFor(screen: UiScreenDTO, viewport: UiViewport) {
  return screen.latestShots.find((s) => s.viewport === viewport);
}

/** Tabulka obrazovek jedné aplikace. */
function AppScreensTable({
  project,
  group,
  onPreview,
  canDelete,
}: {
  project: ProjectDetailDTO;
  group: { slug: string | null; name: string; screens: UiScreenDTO[] };
  onPreview: (screenId: string) => void;
  canDelete: boolean;
}) {
  const approved = group.screens.filter((s) => s.status === "APPROVED").length;
  const thClass = "px-3 py-1.5 text-left af-label-dim font-normal";
  return (
    <Panel data-testid={`screen-group-${group.slug ?? "other"}`}>
      <div className="flex items-baseline justify-between gap-2 px-3 pt-2.5">
        <SectionHeader label={group.name} sub={group.slug ? `// ${group.slug}` : undefined} />
        <span
          className="text-[11px] font-bold"
          style={{
            color: approved === group.screens.length ? "var(--positive)" : "var(--text-secondary)",
          }}
        >
          {cs.pitch.summary(approved, group.screens.length)}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="mt-1.5 w-full border-collapse">
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border-subtle)" }}>
              <th className={thClass}>{cs.pitch.colScreen}</th>
              <th className={thClass}>{cs.pitch.colViewports}</th>
              <th className={thClass}>{cs.pitch.colStatus}</th>
              <th className={thClass} style={{ width: 110 }} />
            </tr>
          </thead>
          <tbody>
            {group.screens.map((screen) => (
              <ScreenRow
                key={screen.id}
                project={project}
                screen={screen}
                onPreview={onPreview}
                canDelete={canDelete}
              />
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function ScreenRow({
  project,
  screen,
  onPreview,
  canDelete,
}: {
  project: ProjectDetailDTO;
  screen: UiScreenDTO;
  onPreview: (screenId: string) => void;
  canDelete: boolean;
}) {
  const queryClient = useQueryClient();
  const deleteMutation = useMutation({
    mutationFn: () => api.deleteScreen(project.id, screen.id),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ["section", project.id, "screens"] }),
  });
  const responsive = isResponsive(screen);
  const thumb = shotFor(screen, "DESKTOP") ?? screen.latestShot ?? undefined;
  return (
    <tr
      className="cursor-pointer"
      style={{ borderBottom: "1px solid var(--border-subtle)" }}
      onClick={() => onPreview(screen.id)}
      data-testid={`screen-${screen.key}`}
    >
      <td className="px-3 py-2">
        <div className="flex items-center gap-2.5">
          {thumb ? (
            <img
              src={api.screenImageUrl(project.id, screen.id, thumb.version)}
              alt=""
              loading="lazy"
              className="block h-9 w-14 shrink-0 object-cover object-top"
              style={{ borderRadius: "var(--radius-box)", border: "1px solid var(--border-default)", background: "var(--surface-inset)" }}
            />
          ) : (
            <span
              className="flex h-9 w-14 shrink-0 items-center justify-center text-[11px]"
              style={{ border: "1px dashed var(--border-default)", color: "var(--text-tertiary)" }}
            >
              —
            </span>
          )}
          <span className="min-w-0">
            <span className="block truncate text-[12px]" style={{ color: "var(--text-primary)" }}>
              {screen.title}
            </span>
            <span className="block truncate text-[11px]" style={{ color: "var(--text-tertiary)" }}>
              {screen.key}
              {screen.routePath ? ` · ${screen.routePath}` : ""}
            </span>
            {screen.originTaskRefs.length > 0 ? (
              <span className="block truncate text-[11px]" style={{ color: "var(--text-tertiary)" }}>
                {cs.pitch.fromTask}{" "}
                {screen.originTaskRefs.map((t, i) => (
                  <Link
                    key={t.id}
                    to="/projects/$slug/tasks/$number"
                    params={{ slug: project.slug, number: String(t.number) }}
                    className="hover:underline"
                    style={{ color: "var(--accent)" }}
                    title={t.title}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`screen-task-${screen.key}-${t.number}`}
                  >
                    {i > 0 ? ", " : ""}#{t.number}
                  </Link>
                ))}
              </span>
            ) : null}
          </span>
        </div>
      </td>
      <td className="px-3 py-2">
        <span className="flex items-center gap-1">
          {responsive ? (
            REQUIRED_VIEWPORTS.map((viewport) => {
              const shot = shotFor(screen, viewport);
              const label = cs.pitch.viewportLabel[viewport] ?? viewport;
              return shot ? (
                <span
                  key={viewport}
                  className="px-1.5 py-0.5 text-[11px] tracking-[0.08em]"
                  style={{ borderRadius: "var(--radius-box)",
                    border: `1px solid ${withAlpha("green", 0.35)}`,
                    color: "var(--positive)",
                  }}
                  title={`${label} · ${cs.pitch.version(shot.version)}`}
                  data-testid={`screen-vp-${screen.key}-${viewport}`}
                >
                  {VIEWPORT_SHORT[viewport]}·{cs.pitch.version(shot.version)}
                </span>
              ) : (
                <span
                  key={viewport}
                  className="px-1.5 py-0.5 text-[11px] tracking-[0.08em]"
                  style={{ border: "1px dashed var(--warning)", color: "var(--warning)" }}
                  title={`${label} · ${cs.pitch.viewportMissing}`}
                  data-testid={`screen-vp-missing-${screen.key}-${viewport}`}
                >
                  {VIEWPORT_SHORT[viewport]}·—
                </span>
              );
            })
          ) : screen.latestShot ? (
            <span className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
              {cs.pitch.version(screen.latestShot.version)}
            </span>
          ) : (
            <span className="text-[11px]" style={{ color: "var(--warning)" }}>
              {cs.pitch.noShot}
            </span>
          )}
        </span>
      </td>
      <td className="px-3 py-2">
        <Chip tone={STATUS_TONE[screen.status]}>{STATUS_LABEL[screen.status]}</Chip>
        {screen.status === "CHANGES_REQUESTED" ? (
          <span className="mt-1 block text-[11px]" style={{ color: "var(--text-tertiary)" }}>
            {screen.feedbackProcessedAt
              ? screen.feedbackTaskRefs.length
                ? `${cs.pitch.processedInto} ${screen.feedbackTaskRefs.map((t) => `#${t.number}`).join(", ")}`
                : cs.feedback.processedEmpty
              : `⏳ ${cs.pitch.awaitingWorker}`}
          </span>
        ) : null}
      </td>
      <td className="px-3 py-2 text-right">
        <span className="flex items-center justify-end gap-2">
          <button
            type="button"
            className="cursor-pointer px-2.5 py-1 text-[11px] tracking-[0.12em]"
            style={{ borderRadius: "var(--radius-box)",
              border: `1px solid ${withAlpha("cyan", 0.4)}`,
              color: "var(--accent)",
              background: withAlpha("cyan", 0.04),
            }}
            onClick={(e) => {
              e.stopPropagation();
              onPreview(screen.id);
            }}
            data-testid={`screen-preview-${screen.key}`}
          >
            {cs.pitch.preview}
          </button>
          {canDelete ? (
            <button
              type="button"
              className="cursor-pointer text-[11px]"
              style={{ color: "var(--critical)" }}
              title={cs.pitch.deleteConfirm(screen.title)}
              disabled={deleteMutation.isPending}
              onClick={(e) => {
                e.stopPropagation();
                if (confirm(cs.pitch.deleteConfirm(screen.title))) deleteMutation.mutate();
              }}
              data-testid={`screen-delete-${screen.key}`}
            >
              ✕
            </button>
          ) : null}
        </span>
      </td>
    </tr>
  );
}

const EMPTY_DRAFTS: Record<UiViewport, string> = { DESKTOP: "", MOBILE: "", TABLET: "" };

/**
 * Preview modal: překlikávání viewportů (desktop/mobil/tablet) s možností
 * napsat připomínku ke každému zvlášť — desktop může být OK a mobil ne.
 */
function ScreenPreviewModal({
  project,
  screen,
  canReview,
  onClose,
}: {
  project: ProjectDetailDTO;
  screen: UiScreenDTO;
  canReview: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const responsive = isResponsive(screen);
  const [active, setActive] = useState<UiViewport>("DESKTOP");
  const [drafts, setDrafts] = useState<Record<UiViewport, string>>(EMPTY_DRAFTS);
  const [generalDraft, setGeneralDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const review = useMutation({
    mutationFn: (input: ReviewScreenInput) => api.reviewScreen(project.id, screen.id, input),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["section", project.id, "screens"] });
      onClose();
    },
    onError: (err) => setError(err instanceof Error ? err.message : cs.common.error),
  });

  // Esc zavírá, šipky přepínají viewporty (mimo psaní v textarea)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (!responsive || (e.target as HTMLElement | null)?.tagName === "TEXTAREA") return;
      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        const idx = REQUIRED_VIEWPORTS.indexOf(active);
        const next = e.key === "ArrowRight" ? (idx + 1) % 3 : (idx + 2) % 3;
        setActive(REQUIRED_VIEWPORTS[next] ?? "DESKTOP");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, responsive, onClose]);

  const activeShot = responsive ? shotFor(screen, active) : (screen.latestShot ?? undefined);
  const activeLabel = responsive
    ? (cs.pitch.viewportLabel[active] ?? active)
    : (cs.pitch.viewportLabel[screen.latestShot?.viewport ?? "DESKTOP"] ?? "");

  const viewportNotes = REQUIRED_VIEWPORTS.flatMap((v) =>
    drafts[v].trim() ? [{ viewport: v, note: drafts[v].trim() }] : [],
  );
  const canSend = responsive ? viewportNotes.length > 0 : generalDraft.trim().length > 0;
  const sendChanges = () =>
    review.mutate(
      responsive
        ? { verdict: "CHANGES_REQUESTED", viewportNotes }
        : { verdict: "CHANGES_REQUESTED", note: generalDraft.trim() },
    );

  // portál do <body>: stránkový wrapper má trvale aktivní transform animaci
  // (af-animate-fade, fill both) → byl by containing blockem pro fixed overlay
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-6"
      style={{ background: "var(--surface-overlay)", backdropFilter: "blur(3px)" }}
      onClick={onClose}
      data-testid="screen-preview-modal"
    >
      <Panel
        brackets
        className="flex max-h-[92vh] w-full max-w-[1040px] flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center gap-2 px-4 py-2.5"
          style={{ borderBottom: "1px solid var(--border-subtle)" }}
        >
          <div className="min-w-0 flex-1">
            <div className="truncate text-[12px]" style={{ color: "var(--text-primary)" }}>
              {screen.title}
            </div>
            <div className="truncate text-[11px]" style={{ color: "var(--text-tertiary)" }}>
              {screen.key}
              {screen.routePath ? ` · ${screen.routePath}` : ""}
              {screen.applicationSlug ? ` · ${screen.applicationSlug}` : ""}
              {screen.originTaskRefs.length > 0 ? (
                <>
                  {" · "}
                  {cs.pitch.fromTask}{" "}
                  {screen.originTaskRefs.map((t, i) => (
                    <Link
                      key={t.id}
                      to="/projects/$slug/tasks/$number"
                      params={{ slug: project.slug, number: String(t.number) }}
                      className="hover:underline"
                      style={{ color: "var(--accent)" }}
                      title={t.title}
                    >
                      {i > 0 ? ", " : ""}#{t.number}
                    </Link>
                  ))}
                </>
              ) : null}
            </div>
          </div>
          <Chip tone={STATUS_TONE[screen.status]} className="shrink-0">
            {STATUS_LABEL[screen.status]}
          </Chip>
          <button
            type="button"
            className="cursor-pointer px-1 text-[13px]"
            style={{ color: "var(--text-secondary)" }}
            onClick={onClose}
            data-testid="screen-preview-close"
          >
            ✕
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 px-4 pt-2">
          {responsive ? (
            REQUIRED_VIEWPORTS.map((viewport) => {
              const shot = shotFor(screen, viewport);
              const isActive = viewport === active;
              const label = cs.pitch.viewportLabel[viewport] ?? viewport;
              return (
                <button
                  key={viewport}
                  type="button"
                  className="cursor-pointer px-3 py-1.5 text-[11px] tracking-[0.14em]"
                  style={{ borderRadius: "var(--radius-box)",
                    border: `1px solid ${isActive ? "var(--accent)" : "var(--border-default)"}`,
                    color: isActive
                      ? "var(--accent)"
                      : shot
                        ? "var(--text-secondary)"
                        : "var(--warning)",
                    background: isActive ? "color-mix(in srgb, var(--accent) 6%, transparent)" : "transparent",
                  }}
                  onClick={() => setActive(viewport)}
                  data-testid={`screen-vp-tab-${viewport}`}
                >
                  {label}
                  <span className="ml-1.5 opacity-70">
                    {shot ? cs.pitch.version(shot.version) : "—"}
                  </span>
                  {drafts[viewport].trim() ? (
                    <span aria-hidden className="ml-1.5" style={{ color: "var(--accent)" }}>
                      ●
                    </span>
                  ) : null}
                </button>
              );
            })
          ) : (
            <span className="af-label-dim">
              {activeLabel}
              {activeShot ? ` · ${cs.pitch.version(activeShot.version)}` : ""}
            </span>
          )}
          {activeShot ? (
            <a
              className="ml-auto text-[11px] tracking-[0.1em]"
              style={{ color: "var(--text-secondary)" }}
              href={api.screenImageUrl(project.id, screen.id, activeShot.version)}
              target="_blank"
              rel="noreferrer"
            >
              {cs.pitch.openFull}
            </a>
          ) : null}
        </div>

        <div
          className="mx-4 mt-2 flex-1 overflow-auto"
          style={{ borderRadius: "var(--radius-box)", minHeight: 220, border: "1px solid var(--border-subtle)", background: "var(--surface-inset)" }}
        >
          {activeShot ? (
            <div data-testid={`screen-shot-${screen.key}-${responsive ? active : activeShot.viewport}`}>
              <img
                src={api.screenImageUrl(project.id, screen.id, activeShot.version)}
                alt={`${screen.title} — ${activeLabel} ${cs.pitch.version(activeShot.version)}`}
                className="mx-auto block h-auto max-w-full"
              />
            </div>
          ) : (
            <div
              className="flex h-full min-h-[220px] items-center justify-center"
              data-testid={`screen-missing-${screen.key}-${active}`}
            >
              <span className="text-[11px] tracking-[0.12em]" style={{ color: "var(--warning)" }}>
                {activeLabel} · {cs.pitch.viewportMissing}
              </span>
            </div>
          )}
        </div>

        {screen.status === "CHANGES_REQUESTED" && screen.reviewNote ? (
          <div
            className="mx-4 mt-2 px-2.5 py-1.5 text-[11px] leading-snug"
            style={{ borderRadius: "var(--radius-box)", border: "1px solid color-mix(in srgb, var(--critical) 25%, transparent)", color: "var(--text-primary)" }}
          >
            <span className="af-label-dim mr-1.5">{cs.pitch.savedNote}</span>
            <span className="whitespace-pre-line">{screen.reviewNote}</span>
            <div className="mt-1 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
              {screen.feedbackProcessedAt
                ? screen.feedbackTaskRefs.length
                  ? `${cs.pitch.processedInto} ${screen.feedbackTaskRefs.map((t) => `#${t.number}`).join(", ")}`
                  : cs.feedback.processedEmpty
                : `⏳ ${cs.pitch.awaitingWorker}`}
            </div>
          </div>
        ) : null}

        {canReview ? (
          <div className="px-4 pt-2">
            {responsive ? (
              <>
                <div className="af-label-dim mb-1">
                  {cs.pitch.statusChanges} · {activeLabel}
                </div>
                <textarea
                  className={`${inputClass} min-h-[52px] resize-y`}
                  value={drafts[active]}
                  onChange={(e) => setDrafts((d) => ({ ...d, [active]: e.target.value }))}
                  placeholder={cs.pitch.viewportNotePlaceholder(activeLabel)}
                  data-testid={`screen-note-${screen.key}-${active}`}
                />
              </>
            ) : (
              <>
                <div className="af-label-dim mb-1">{cs.pitch.generalNote}</div>
                <textarea
                  className={`${inputClass} min-h-[52px] resize-y`}
                  value={generalDraft}
                  onChange={(e) => setGeneralDraft(e.target.value)}
                  placeholder={cs.pitch.notePlaceholder}
                  data-testid={`screen-note-${screen.key}`}
                />
              </>
            )}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center justify-end gap-2 px-4 py-3">
          {error ? (
            <span className="mr-auto text-[11px]" style={{ color: "var(--critical)" }}>
              ⚠ {error}
            </span>
          ) : null}
          <Btn onClick={onClose}>{cs.pitch.close}</Btn>
          {canReview ? (
            <>
              {screen.status === "APPROVED" ? (
                <Btn
                  disabled={review.isPending}
                  onClick={() => review.mutate({ verdict: "PENDING" })}
                  data-testid={`screen-reopen-${screen.key}`}
                >
                  {cs.pitch.reopen}
                </Btn>
              ) : null}
              <Btn
                tone="red"
                disabled={!canSend || review.isPending}
                onClick={sendChanges}
                data-testid={`screen-note-send-${screen.key}`}
              >
                {cs.pitch.sendNote}
              </Btn>
              {screen.status !== "APPROVED" ? (
                <Btn
                  variant="primary"
                  disabled={review.isPending}
                  onClick={() => review.mutate({ verdict: "APPROVED" })}
                  data-testid={`screen-approve-${screen.key}`}
                >
                  {cs.pitch.approve}
                </Btn>
              ) : null}
            </>
          ) : null}
        </div>
      </Panel>
    </div>,
    document.body,
  );
}
